/**
 * Button Component
 *
 * Simple, reusable button with variants.
 * No external CSS libraries - just inline styles for simplicity.
 */

import React from 'react';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'danger';
  fullWidth?: boolean;
  isLoading?: boolean;
}

export const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  fullWidth = false,
  isLoading = false,
  disabled,
  style,
  ...props
}) => {
  const baseStyles: React.CSSProperties = {
    padding: '12px 24px',
    fontSize: '16px',
    fontWeight: 500,
    borderRadius: '8px',
    border: 'none',
    cursor: disabled || isLoading ? 'not-allowed' : 'pointer',
    transition: 'all 0.2s',
    width: fullWidth ? '100%' : 'auto',
    opacity: disabled || isLoading ? 0.6 : 1,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  };

  const variantStyles: Record<typeof variant, React.CSSProperties> = {
    primary: {
      backgroundColor: '#4285f4',
      color: '#fff',
    },
    secondary: {
      backgroundColor: '#f1f3f4',
      color: '#202124',
    },
    danger: {
      backgroundColor: '#ea4335',
      color: '#fff',
    },
  };

  return (
    <button
      style={{ ...baseStyles, ...variantStyles[variant], ...style }}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading && <span>⏳</span>}
      {children}
    </button>
  );
};
